<template>
  <div class="auto-update-card box">
    <!-- Header -->
    <div class="card-header-row">
      <span class="card-title">{{ $t('autoUpdate.title') || 'Auto Update' }}</span>
      <button
        class="refresh-btn"
        :disabled="loading"
        @click="fetchSettings"
      >
        <octicon :icon="syncIcon" :class="{ 'spin': loading }" />
      </button>
    </div>

    <!-- Error State -->
    <div v-if="error" class="notification is-danger is-light mt-3">
      <p>{{ error }}</p>
      <button class="button is-small mt-2" @click="fetchSettings">
        <octicon :icon="syncIcon" />
        <span>{{ $t('retry') || 'Retry' }}</span>
      </button>
    </div>

    <!-- Content -->
    <div v-else class="card-content-section">
      <!-- Toggle -->
      <div class="toggle-row">
        <span class="toggle-label">{{ $t('autoUpdate.enabled') || 'Enabled' }}</span>
        <div class="toggle-control">
          <button
            class="button is-small"
            :class="settings.enabled ? 'is-success' : 'is-light'"
            :disabled="saving"
            @click="toggleEnabled"
          >
            {{ settings.enabled ? ($t('enabled') || 'Enabled') : ($t('disabled') || 'Disabled') }}
          </button>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import { sync, alert } from 'octicons-vue'

export default {
  name: 'AutoUpdateSettingsCard',
  data () {
    return {
      settings: {
        key: 'auto_update',
        enabled: false,
        state: 'idle',
        check_interval_seconds: 300,
        last_run_at: null,
        last_status: 'never',
        last_error: '',
        updated_at: null,
        updated_by: ''
      },
      loading: false,
      saving: false,
      error: null
    }
  },
  computed: {
    syncIcon () {
      return sync
    },
    alertIcon () {
      return alert
    },
    isRoot () {
      const user = this.$store.getters['authentication/user']
      if (!user || !user.scopes) return false
      const scopes = Array.isArray(user.scopes) ? user.scopes : []
      return scopes.includes('root') || scopes.includes('*:*')
    },
    stateTagClass () {
      switch (this.settings.state) {
        case 'idle': return 'is-info is-light'
        case 'running': return 'is-warning'
        case 'blocked': return 'is-danger'
        default: return 'is-light'
      }
    },
    lastStatusTagClass () {
      switch (this.settings.last_status) {
        case 'success': return 'is-success is-light'
        case 'failed': return 'is-danger is-light'
        case 'never': return 'is-light'
        default: return 'is-light'
      }
    }
  },
  mounted () {
    this.fetchSettings()
  },
  methods: {
    async fetchSettings () {
      try {
        this.loading = true
        this.error = null
        const response = await this.$http.get('/api/v1/system/settings/auto-update')
        const data = response.data && response.data.data ? response.data.data : response.data
        if (data) {
          this.settings = data
        }
      } catch (err) {
        console.error('Failed to fetch auto-update settings:', err)
        this.error = (err && err.message) || 'Failed to fetch settings'
      } finally {
        this.loading = false
      }
    },
    async toggleEnabled () {
      const newEnabled = !this.settings.enabled
      try {
        this.saving = true
        await this.$http.put('/api/v1/system/settings/auto-update', {
          enabled: newEnabled
        })
        if (this.$toast) {
          this.$toast(
            this.$t('autoUpdate.updated') || 'Auto-update settings updated',
            'success',
            3000
          )
        }
        await this.fetchSettings()
      } catch (err) {
        const msg = (err && err.body && err.body.message) ||
          (this.$t('autoUpdate.updateFailed') || 'Failed to update auto-update settings')
        if (this.$toast) {
          this.$toast(msg, 'error', 5000)
        }
      } finally {
        this.saving = false
      }
    },
    formatDateTime (val) {
      if (!val) return 'N/A'
      const d = new Date(val)
      if (isNaN(d.getTime())) return 'N/A'
      return d.toLocaleString()
    }
  }
}
</script>

<style lang="scss" scoped>
.auto-update-card {
  padding: 1rem;
  border-radius: 6px;
  background: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  .is-dark & {
    background: #2c2c2c;
    color: #eee;
  }

  .card-header-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
    padding-bottom: 0.5rem;
    border-bottom: 1px solid rgba(0, 0, 0, 0.1);

    .is-dark & {
      border-bottom-color: rgba(255, 255, 255, 0.1);
    }
  }

  .card-title {
    font-weight: 600;
    font-size: 1rem;
  }

  .refresh-btn {
    background: transparent;
    border: 1px solid #ddd;
    border-radius: 4px;
    padding: 0.25rem 0.5rem;
    cursor: pointer;
    color: inherit;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
      background: rgba(0, 0, 0, 0.05);
    }

    &:disabled {
      cursor: not-allowed;
      opacity: 0.5;
    }

    .is-dark & {
      border-color: #555;

      &:hover {
        background: rgba(255, 255, 255, 0.1);
      }
    }
  }

  .card-content-section {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .toggle-row,
  .info-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.35rem 0;
  }

  .toggle-label,
  .info-label {
    font-size: 0.875rem;
    color: #555;

    .is-dark & {
      color: #bbb;
    }
  }

  .info-value {
    font-size: 0.875rem;
  }

  .error-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.25rem;

    .info-value {
      word-break: break-all;
    }
  }

  .spin {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
}
</style>
